'use client'

import { useState, useEffect } from "react"
import { usePathname } from "next/navigation"
import { Button } from "@/components/elements/Button"
import { formatPrice } from "@/utils/util"
import { BadgePercent, Check, Info, Medal, PackageX, ShoppingBag } from "lucide-react"
import { navigateUrl } from "@/utils/client-request"
import { useCart, useLoginForm } from "@/lib/commerce/hooks"
import Input from "@/components/elements/Input"
import Link from "next/link"
import CheckoutCard from "./CheckoutCard"
import CartEmpty from "@/assets/cart-empty.png"
import Image from "next/image"
import { Progress } from "@/components/ui/progress"
import Skeleton from "@/components/elements/Skeleton"

export default function CheckoutLogin({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const { cart, initialLoading, isValidating, validateCart, canCheckout, amount, sale, activeOrderDiscount, orderDiscountAmount, appliedCoupon, cartTotal, loyaltyDiscountAmount } = useCart()
  const loyaltyDiscount = loyaltyDiscountAmount > 0 ? Math.min(loyaltyDiscountAmount, Math.max(0, cartTotal - orderDiscountAmount)) : 0
  const f = useLoginForm({ defaultRedirect: '/checkout' })
  const [hasMounted, setHasMounted] = useState(false)

  const products = cart?.products || []

  useEffect(() => { setHasMounted(true) }, [])

  useEffect(() => {
    if (products.length > 0 && vendor) validateCart()
  }, [products.length, vendor])

  if (!hasMounted) return null

  return (
    <div className="cont py-8 sm:py-8">
      <div className="lg:grid lg:grid-cols-12 lg:gap-x-8 space-y-8 lg:space-y-0 pb-8 sm:pb-0">
        <div className="lg:col-span-6">
          <div className="w-full max-w-md mx-auto">
            <div className="flex flex-col items-center gap-6">
              <h1 className="text-center text-foreground text-2xl font-semibold leading-7.5 tracking-[-0.7%]">Нэвтрэх</h1>
            </div>
            <div className="mt-10">
              <div className="flex flex-col gap-4">
                <form onSubmit={f.handleLogin} className="flex flex-col gap-y-4">
                  <Input label="Утасны дугаар / Имэйл" type="text" placeholder="Утасны дугаар эсвэл имэйл хаяг" value={f.emailOrPhone} onChange={(e: any) => f.setEmailOrPhone(e.target.value)} onKeyDown={f.handleKeyDown} autoFocus />
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium leading-none">Нууц үг</span>
                      <Link href={navigateUrl(`/forgot`, pathname)} className="text-xs font-medium text-foreground hover:text-foreground/90 transition">
                        Нууц үгээ мартсан уу?
                      </Link>
                    </div>
                    <Input type="password" placeholder="Таны нэвтрэх нууц үг" value={f.password} onChange={(e: any) => f.setPassword(e.target.value)} onKeyDown={f.handleKeyDown} />
                  </div>
                  <Button type="submit" isLoading={f.loading} className="mt-2 w-full" loadingMessage="Нэвтэрч байна..." color={vendor?.color?.primary} border={vendor?.border}>
                    Нэвтрэх
                  </Button>
                </form>
                {vendor?.canGuestOrder && (
                  <>
                    <div className="flex items-center justify-center gap-2">
                      <div className="grow basis-0 border-b border-neutral-200" />
                      <span className="text-sm font-medium leading-5 tracking-[-0.5%] text-muted-foreground">Эсвэл</span>
                      <div className="grow basis-0 border-b border-neutral-200" />
                    </div>
                    <Button onClick={() => { window.location.href = navigateUrl('/checkout', pathname) }} className="w-full" variant="outline" color={vendor?.color?.primary} border={vendor?.border} disabled={isValidating || !canCheckout()}>
                      Зочноор нэвтрэх
                    </Button>
                  </>
                )}
              </div>
            </div>
            <p className="mt-4 text-center text-foreground leading-5 text-xs">
              Шинэ хэрэглэгч үү?{' '}
              <span onClick={f.routeToRegister} className="cursor-pointer font-medium text-foreground underline hover:text-foreground/90 leading-5 tracking-[-0.5%] transition-colors">
                Бүртгэл үүсгэх
              </span>
            </p>
            {vendor?.canGuestOrder && (
              <div className="mt-6">
                <div className="flex gap-3 p-3 bg-amber-50 border border-amber-100 rounded-lg text-warning">
                  <Info size={14} className="shrink-0 mt-0.5" />
                  <div className="text-xs">
                    Зочноор нэвтэрсэн тохиолдолд таны захиалгын түүх хадгалагдахгүй болно.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="lg:col-span-1">
          <div className="border-b border-dashed border-border lg:hidden"></div>
          <div className="hidden lg:block h-full border-l border-border"></div>
        </div>
        <div className="lg:col-span-5">
          {initialLoading ? (
            <div className="flex flex-col space-y-6">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="flex items-center gap-3">
                  <Skeleton className="w-24 h-24 rounded-lg shrink-0" />
                  <div className="flex flex-col w-full gap-2">
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-5 w-20" />
                    </div>
                    <Skeleton className="h-4 w-24" />
                    <div className="flex justify-end">
                      <Skeleton className="h-8 w-28 rounded-md" />
                    </div>
                  </div>
                </div>
              ))}
              <div className="flex flex-col gap-3 mt-7">
                <div className="flex justify-between">
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-24" />
                </div>
                <div className="flex justify-between">
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-32" />
                </div>
                <div className="flex justify-between">
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-24" />
                </div>
              </div>
            </div>
          ) : products?.length > 0 ? (
            <>
              <div className="flex flex-col space-y-6">
                {products?.map((product: any, index: number) => (
                  <CheckoutCard key={index} product={product} />
                ))}
              </div>
              <div className="flex flex-col gap-3 mt-7">
                <div className="flex items-center justify-between font-semibold text-foreground leading-7 text-base">
                  <div>Үнийн дүн</div>
                  <div>{formatPrice(cartTotal)}</div>
                </div>
                {!products.every((p: any) => p.isDigital === true) && (
                  <div className="flex items-center justify-between font-semibold text-foreground leading-7 text-base">
                    <div>Хүргэлт</div>
                    <div className="text-muted-foreground">Хүргэлтийн хаяг оруулах</div>
                  </div>
                )}
                {orderDiscountAmount > 0 && (
                  <div className="flex items-center justify-between text-green-600 font-semibold leading-7 text-base">
                    <div>Захиалгын хөнгөлөлт</div>
                    <div>-{formatPrice(orderDiscountAmount)}</div>
                  </div>
                )}
                {loyaltyDiscount > 0 && (
                  <div className="flex items-center justify-between text-violet-600 font-semibold leading-7 text-base">
                    <div className="flex items-center gap-1.5"><Medal className="w-4 h-4" />Лоялти хөнгөлөлт</div>
                    <div>-{formatPrice(loyaltyDiscount)}</div>
                  </div>
                )}
                <div className="flex items-center justify-between font-semibold text-foreground leading-7 text-base">
                  <div>Нийт дүн</div>
                  <div>{formatPrice(Math.max(0, cartTotal - orderDiscountAmount - loyaltyDiscount))}</div>
                </div>
                {activeOrderDiscount?.type === 'order' && !appliedCoupon && activeOrderDiscount.minOrderAmount > 0 && (() => {
                  const subPrice = amount - sale
                  const minAmt = activeOrderDiscount.minOrderAmount
                  const target = activeOrderDiscount.targets?.find((t: any) => t.role === 'target' && t.targetId === null)
                  if (!target) return null
                  const discountLabel = target.valueType === 'percent' ? `${target.valuePercent}%-ийн хөнгөлөлт` : target.valueType === 'amount' ? `${formatPrice(target.valueAmount)} хөнгөлөлт` : null
                  if (!discountLabel) return null
                  const reached = subPrice >= minAmt
                  return (
                    <div className="flex flex-col gap-2.5 p-3 bg-purple-50 rounded-lg">
                      <div className="flex items-start gap-1.5 text-purple-700 text-sm">
                        <BadgePercent className="w-4 h-4 shrink-0 mt-0.5" />
                        <span className="font-medium">{formatPrice(minAmt)}-с дээш захиалгад {discountLabel}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Progress value={Math.min((subPrice / minAmt) * 100, 100)} className="h-2 flex-1 bg-purple-200 [&>div]:bg-purple-600" />
                        {reached ? (
                          <div className="flex items-center gap-1 text-purple-700 text-xs shrink-0">
                            <Check className="w-3.5 h-3.5" />
                            <span className="font-medium">-{formatPrice(orderDiscountAmount)}</span>
                          </div>
                        ) : (
                          <span className="text-purple-700 text-xs font-medium shrink-0">{formatPrice(minAmt - subPrice)} дутуу</span>
                        )}
                      </div>
                    </div>
                  )
                })()}
                {cart?.products?.some((product: any) => product.quantity === 0 || product.isOutOfStock) && (
                  <div className="p-4 bg-red-50 border border-red-100 rounded-lg">
                    <div className="flex items-start gap-3">
                      <PackageX className='w-5 h-5 text-red-500 shrink-0 mt-1' />
                      <div className="flex flex-col gap-1">
                        <span className="text-sm text-red-500 font-medium leading-6">Үлдэгдэл дууссан бараа</span>
                        <span className='text-sm text-red-500 font-normal leading-5'>Таны сагсанд үлдэгдэл хүрэлцэхгүй бараа байна, шалгаад үргэлжлүүлнэ үү</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center">
              <Image src={CartEmpty} alt="Cart Empty" className="w-48 h-48 mb-4" />
              <div className="text-foreground text-center text-lg leading-8 tracking-[.0125em] font-gilroy font-semibold">Таны сагс хоосон байна</div>
              <div className="mt-1 font-normal text-sm text-muted-foreground text-center">Та яг одоо сагсандаа бараа нэмээрэй!</div>
              <Button onClick={() => { window.location.href = navigateUrl('/product', pathname) }} className="mt-6" color={vendor?.color?.primary} border={vendor?.border} icon={ShoppingBag}>
                Дэлгүүр хэсэх
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
